import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { CardLayout, MTable } from "../v3";
import { vendorReportColumns } from "../../pages/report/constants";
import { get_date } from "../generalComponets";
import { AnalyticsService } from "../../services";
import { downloadFile } from "../../utils/download.utils";

export const VendorReportResults = ({
  reportType,
  data = {},
  loading = false,
  paginationState,
  handlePaginationChange = () => {},
  reportFilters = {},
}) => {
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [prevFilters, setPrevFilters] = useState({});

  useEffect(() => {
    if (pdfSuccess && JSON.stringify(reportFilters) !== JSON.stringify(prevFilters)) {
      setPdfSuccess(false);
    }
  }, [reportFilters, prevFilters, pdfSuccess]);

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    setPdfSuccess(false);
    try {
      const response = await AnalyticsService.downloadVendorReportPdf(reportFilters || {});

      const currentDateTime = moment().format("YYYYMMDD-HHmmss");
      const filename = `vendor-report-${reportType}-${currentDateTime}.pdf`;

      downloadFile(response.data, filename);
      toast.success("PDF downloaded successfully");

      setPdfSuccess(true);
      setPrevFilters(reportFilters);
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to download PDF");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const rows =
    data?.rows?.map((row, index) => ({
      id: index + 1 + paginationState.page * paginationState.pageSize,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      formattedDate: get_date(row.date),
      ...row,
    })) || [];

  const totalOpeningBalance = rows.reduce((sum, row) => sum + Number(row.openingBalance || 0), 0);
  const totalPurchaseAmount = rows.reduce((sum, row) => sum + Number(row.totalPurchase || 0), 0);
  const totalReturnAmount = rows.reduce((sum, row) => sum + Number(row.totalReturn || 0), 0);
  const totalPaidAmount = rows.reduce((sum, row) => sum + Number(row.totalPaid || 0), 0);
  const totalClosingBalance = rows.reduce((sum, row) => sum + Number(row.closingBalance || 0), 0);
  const totalPurchase = rows.reduce((sum, row) => sum + Number(row.purchaseAmount || 0), 0);
  const totalReturn = rows.reduce((sum, row) => sum + Number(row.returnAmount || 0), 0);
  const totalPayment = rows.reduce((sum, row) => sum + Number(row.paymentAmount || 0), 0);

  const subtotal =
    data?.count > 0
      ? [
          {
            id: "subtotal",
            label: "Subtotal",
            totalOpeningBalance: totalOpeningBalance.toFixed(2),
            totalPurchaseAmount: totalPurchaseAmount.toFixed(2),
            totalReturnAmount: totalReturnAmount.toFixed(2),
            totalPaidAmount: totalPaidAmount.toFixed(2),
            totalClosingBalance: totalClosingBalance.toFixed(2),
            totalPurchase: totalPurchase.toFixed(2),
            totalReturn: totalReturn.toFixed(2),
            totalPayment: totalPayment.toFixed(2),
          },
        ]
      : [];

  const allRows = [...rows, ...subtotal];
  return (
    <CardLayout>
      <MTable
        // Export data
        showExportButton // TODO: implement permission
        isPdfGenerating={isPdfGenerating}
        pdfSuccess={pdfSuccess}
        currentFilters={reportFilters}
        prevFilters={prevFilters}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={vendorReportColumns[reportType]}
        tableRows={allRows}
        rowCount={data.count}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
